import { useEffect, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { Button, Group, Modal, ScrollAreaAutosize, Text } from "@mantine/core";
import {
  Item as RadioGroupItem,
  Root as RadioGroupRoot,
} from "@radix-ui/react-radio-group";
import { Row } from "@tanstack/react-table";
import { useShallow } from "zustand/react/shallow";

import { getViewProfileReasons } from "@data/dropdownValues";

import { useAppStore } from "@hooks/store/useAppStore";

import { useAuth } from "@contexts/auth";

import { Patient } from "@customTypes/patient";

import { StyledViewProfileModalContent } from "./ViewProfileModal.styled";


interface ViewProfileModalProps {
  row: Row<Partial<Patient>> | Record<string, never>;
  rowId: string;
  reason: string;
  setReason: (arg: string) => void;
  opened: boolean;
  close: () => void;
}

export const ViewProfileModal = ({
  row,
  rowId,
  reason,
  setReason,
  opened,
  close,
}: ViewProfileModalProps) => {
  const { t } = useTranslation();
  // console.log(row.id, rowId);
  const { id: userId } = useAuth();
  const [users, setPatientReason] = useAppStore(
    useShallow(state => [state.users, state.setPatientReason])
  );

  const navigate = useNavigate();

  const [hasSelectedReason, setHasSelectedReason] = useState(false);

  useEffect(() => {
    if (row.id !== rowId) return;
    // console.log("check");
    if (!reason && row?.original?.patientId && rowId === row.id) {
      const reasonFromStore =
        users[userId]?.patients[row.original.patientId]?.reason ?? undefined;
      if (reasonFromStore) setReason(reasonFromStore);
    } else if (reason && row?.original?.patientId && rowId === row.id) {
      setPatientReason(userId, row.original.patientId, reason);
    }
  }, [reason, row, rowId]);

  // Prevent other rows from rendering this component
  if (row.id !== rowId) return;

  const name = row?.original?.firstName;

  return (
    <Modal
      centered
      title={<Text fw={500}>{t("view-profile")}</Text>}
      opened={opened}
      onClose={close}
      scrollAreaComponent={ScrollAreaAutosize}
      styles={{ body: { minHeight: "auto" } }}>
      <StyledViewProfileModalContent className="DialogContent">
        <Text fz={15} className="description" lh={1.3}>
          <Trans
            i18nKey={"view-profile-reason"}
            values={{ name }}
            components={{ span: <span /> }}
          />
        </Text>

        <form className="reasons">
          <RadioGroupRoot
            className="RadioGroupRoot"
            value={reason}
            onValueChange={val => {
              setReason(val);
            }}>
            {getViewProfileReasons().map(item => (
              <RadioGroupItem
                key={item.value}
                className={`RadioGroupItem`}
                value={item.value}
                onClick={() => setHasSelectedReason(true)}>
                {item.label}
              </RadioGroupItem>
            ))}
          </RadioGroupRoot>
        </form>

        <Group justify="end" mt="xl">
          <Button
            disabled={!hasSelectedReason && !reason}
            onClick={() => {
              close();
              setHasSelectedReason(false);
              navigate(`${row.original.patientId}/profile`);
            }}>
            {t("view-profile")}
          </Button>
        </Group>
      </StyledViewProfileModalContent>
    </Modal>
  );
};
